import { useEffect, useState, useCallback } from "react";
import api, { apiError, API } from "@/lib/api";
import TripForm from "@/components/TripForm";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Truck, FileText, Route, GitMerge, Tag, ShoppingBag,
  CreditCard, Wallet, Star, Globe, Settings as Cog, Plus, Upload, Download,
  Copy, Trash2, Power, TrendingUp, Package, Euro, Mail, Camera, CheckCircle2, Ticket, AlertTriangle, Archive, CalendarDays,
} from "lucide-react";
import { ShareRoute } from "@/components/ShareRoute";
import { fmtDate } from "@/lib/utils";
import { photoUrl } from "@/components/PhotoUpload";
import { AdminStats } from "@/components/AdminStats";
import { AdminCoupons } from "@/components/AdminCoupons";
import { AdminClaims } from "@/components/AdminClaims";
import { AdminRequests } from "@/components/AdminRequests";
import { AdminCustomers } from "@/components/AdminCustomers";
import { AdminBookings } from "@/components/AdminBookings";
import { AdminArchive } from "@/components/AdminArchive";
import { AdminEmails } from "@/components/AdminEmails";
import { AdminCalendar } from "@/components/AdminCalendar";

const MENU = [
  ["dashboard", "Dashboard", LayoutDashboard], ["customers", "Kunden", Users],
  ["partners", "Transportpartner", Truck], ["requests", "Anfragen", FileText],
  ["trips", "Fahrten", Truck], ["routes", "Unsere Routen", Route], ["calendar", "Routen-Kalender", CalendarDays],
  ["offers", "Matches & Angebote", GitMerge], ["bookings", "Buchungen", ShoppingBag],
  ["payments", "Zahlungen", CreditCard], ["payouts", "Auszahlungen", Wallet],
  ["emails", "E-Mail-Versand", Mail], ["coupons", "Gutscheine", Ticket], ["claims", "Schadensfälle", AlertTriangle], ["archive", "Archiv", Archive],
  ["reviews", "Bewertungen", Star], ["cms", "CMS / SEO", Globe], ["settings", "Einstellungen", Cog],
];

export default function Admin() {
  const qs = new URLSearchParams(window.location.search);
  const [tab, setTab] = useState(MENU.some(([k]) => k === qs.get("tab")) ? qs.get("tab") : "dashboard");
  const initialId = qs.get("id");
  const [unanswered, setUnanswered] = useState(0);
  useEffect(() => {
    const load = () => api.get("/admin/requests/unanswered").then((r) => setUnanswered(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    const onSent = () => load();
    window.addEventListener("request-message-sent", onSent);
    return () => { clearInterval(t); window.removeEventListener("request-message-sent", onSent); };
  }, []);
  return (
    <div className="eb-container py-8">
      <h1 className="font-head text-3xl font-bold mb-6">Adminbereich</h1>
      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-60 shrink-0">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0">
            {MENU.map(([k, l, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`admin-menu-${k}`}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${tab===k?"bg-[#FF5A1F] text-white":"text-slate-400 hover:text-white hover:bg-white/5"}`}>
                <Icon className="w-4 h-4" /> {l}
                {k === "requests" && unanswered > 0 && <span className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-bold ${tab===k?"bg-white text-[#FF5A1F]":"bg-[#FF5A1F] text-white"}`} title="Unbeantwortete Kundennachrichten" data-testid="admin-unanswered-badge">{unanswered}</span>}
              </button>
            ))}
          </nav>
        </aside>
        <div className="flex-1 min-w-0">
          {tab === "dashboard" && <Dashboard />}
          {tab === "customers" && <AdminCustomers useData={useData} />}
          {tab === "archive" && <AdminArchive useData={useData} />}
          {tab === "partners" && <Partners />}
          {tab === "requests" && <AdminRequests useData={useData} initialId={initialId} />}
          {tab === "trips" && <SimpleTable endpoint="/admin/trips" cols={[["partner_name","Anbieter"],["start_ort","Von"],["ziel_ort","Nach"],["abfahrt","Abfahrt"],["freie_m3","Frei m³"],["status","Status"]]} testid="trips" />}
          {tab === "routes" && <OwnRoutes />}
          {tab === "calendar" && <AdminCalendar onCreate={() => setTab("routes")} />}
          {tab === "offers" && <SimpleTable endpoint="/admin/offers" cols={[["route_label","Route"],["match_score","Score %"],["partner_price","Partner €"],["commission","Provision €"],["customer_price","Kunde €"],["status","Status"]]} testid="offers" />}
          {tab === "bookings" && <AdminBookings useData={useData} />}
          {tab === "payments" && <SimpleTable endpoint="/admin/payments" cols={[["booking_id","Buchung"],["provider","Anbieter"],["amount","Betrag €"],["mode","Art"],["payment_status","Status"],["created_at","Datum"]]} testid="payments" />}
          {tab === "emails" && <AdminEmails useData={useData} />}
          {tab === "payouts" && <Payouts />}
          {tab === "coupons" && <AdminCoupons useData={useData} />}
          {tab === "claims" && <AdminClaims useData={useData} />}
          {tab === "reviews" && <Reviews />}
          {tab === "cms" && <Cms />}
          {tab === "settings" && <SettingsPanel />}
        </div>
      </div>
    </div>
  );
}

export function useData(endpoint) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(endpoint); setData(data); } catch { setData([]); }
    setLoading(false);
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

function Dashboard() {
  const { data } = useData("/admin/dashboard");
  const backup = async () => {
    const { data: b } = await api.get("/admin/backup");
    const url = URL.createObjectURL(new Blob([JSON.stringify(b)], { type: "application/json" }));
    const a = document.createElement("a"); a.href = url; a.download = `easybeiladung-backup-${b.created_at.slice(0, 10)}.json`; a.click();
    toast.success("Sicherung heruntergeladen");
  };
  const cards = [
    ["Neue Anfragen", data.neue_anfragen, FileText], ["Offene Matches", data.offene_matches, GitMerge],
    ["Aktive Transporte", data.aktive_transporte, Truck], ["Buchungen", data.buchungen, ShoppingBag],
    ["Umsatz", `${data.umsatz || 0} €`, Euro], ["Provision", `${data.provision || 0} €`, TrendingUp],
    ["Aktive Routen", data.aktive_routen, Route], ["Freie Kapazität", `${data.freie_kapazitaet || 0} m³`, Package],
    ["Kunden", data.kunden, Users], ["Partner", data.partner, Truck],
  ];
  return (
    <div data-testid="admin-dashboard">
      <div className="flex justify-end mb-4"><button onClick={backup} className="eb-btn-outline text-xs" data-testid="backup-download-btn"><Download className="w-4 h-4" /> Sicherung herunterladen (JSON)</button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map(([l, v, Icon]) => (
          <div key={l} className="eb-card p-5">
            <div className="flex items-center justify-between mb-2"><span className="eb-label">{l}</span><Icon className="w-4 h-4 text-[#FF5A1F]" /></div>
            <div className="font-head text-3xl font-bold">{v ?? 0}</div>
          </div>
        ))}
      </div>
      <AdminStats />
    </div>
  );
}

function fmt(v, key, row = {}) {
  if (key === "photos") {
    if (!Array.isArray(v) || !v.length) return "–";
    return <span className="flex gap-1">{v.map((id) => <a key={id} href={photoUrl(id)} target="_blank" rel="noreferrer"><img src={photoUrl(id)} alt="" className="w-8 h-8 rounded object-cover border border-slate-700" /></a>)}</span>;
  }
  if (key === "provider") return v || "stripe";
  if (key === "status" && row.expired) return <span className="eb-badge bg-slate-700 text-slate-300" data-testid={`trip-expired-${row.id}`}>abgelaufen ({v})</span>;
  if (key === "contact_email" && v) return <a href={`mailto:${v}`} className="text-[#FF5A1F] underline">{v}</a>;
  if (key === "contact_phone" && v) return <a href={`tel:${String(v).replace(/\s/g, "")}`} className="text-[#FF5A1F] underline">{v}</a>;
  if (key === "invoice_number" && v) return <a href={`${API}/bookings/${row.id}/invoice`} target="_blank" rel="noreferrer" className="text-[#FF5A1F] underline">{v}</a>;
  if (v == null) return "–";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return fmtDate(v);
  return String(v);
}

function SimpleTable({ endpoint, cols, testid }) {
  const { data, loading } = useData(endpoint);
  if (loading) return <div className="text-slate-400">Lädt…</div>;
  return (
    <div className="eb-card overflow-hidden" data-testid={`admin-table-${testid}`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800 text-left">{cols.map(([k, l]) => <th key={k} className="px-4 py-3 eb-label">{l}</th>)}</tr></thead>
          <tbody>
            {data.length === 0 ? <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-slate-500">Keine Daten</td></tr> :
              data.map((row, i) => (
                <tr key={i} className="border-b border-slate-800/50 hover:bg-white/[0.02]">
                  {cols.map(([k]) => <td key={k} className="px-4 py-3 text-slate-300">{fmt(row[k], k, row)}</td>)}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OwnRoutes() {
  const { data, loading, reload } = useData("/admin/trips");
  const own = data.filter((t) => t.owner_type === "admin");
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [csv, setCsv] = useState("");

  const create = async (d) => { try { await api.post("/trips", { ...d, owner_type: "admin" }); toast.success("Route angelegt"); setShow(false); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const update = async (d) => { try { await api.put(`/trips/${edit.id}`, d); toast.success("Route aktualisiert"); setEdit(null); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const del = async (id) => { await api.delete(`/trips/${id}`); toast.success("Route gelöscht"); reload(); };
  const dup = async (id) => { await api.post(`/trips/${id}/duplicate`); toast.success("Route dupliziert"); reload(); };
  const toggle = async (t) => {
    try { await api.post(`/trips/${t.id}/status?status=${t.status === "aktiv" ? "inaktiv" : "aktiv"}`); toast.success(t.status === "aktiv" ? "Route deaktiviert" : "Route ist jetzt live und öffentlich sichtbar"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doExport = async () => {
    const { data } = await api.get("/admin/routes/export");
    const blob = new Blob([data.csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "eigene-routen.csv"; a.click();
    toast.success(`${data.count} Routen exportiert`);
  };
  const doImport = async () => {
    if (!csv.trim()) return toast.error("Bitte CSV einfügen");
    const { data } = await api.post("/admin/routes/import", { csv });
    toast.success(`${data.imported} Routen importiert`); setCsv(""); reload();
  };

  return (
    <div data-testid="admin-own-routes">
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => { setShow(!show); setEdit(null); }} className="eb-btn-primary" data-testid="route-add-btn"><Plus className="w-4 h-4" /> Route hinzufügen</button>
        <button onClick={doExport} className="eb-btn-outline" data-testid="route-export-btn"><Download className="w-4 h-4" /> CSV exportieren</button>
        {own.some((t) => t.legacy_demo || t.notes === "Eigene Route") && <button onClick={async () => { const { data } = await api.delete("/admin/trips/legacy-demo"); toast.success(`${data.deleted} Demo-Routen gelöscht`); reload(); }} className="eb-btn-outline text-red-400" data-testid="route-delete-demo-btn"><Trash2 className="w-4 h-4" /> Demo-Routen löschen</button>}
      </div>

      {(show || edit) && <div className="eb-card p-6 mb-6"><h3 className="font-head font-semibold mb-4">{edit ? "Route bearbeiten" : "Neue eigene Route (OK Umzüge)"}</h3>
        <TripForm initial={edit} onSubmit={edit ? update : create} onCancel={() => { setShow(false); setEdit(null); }} submitLabel={edit ? "Speichern" : "Route anlegen"} /></div>}

      <div className="eb-card p-5 mb-6">
        <h3 className="font-head font-semibold mb-2 flex items-center gap-2"><Upload className="w-4 h-4 text-[#FF5A1F]" /> CSV importieren</h3>
        <p className="text-xs text-slate-500 mb-3">Spalten: route_id, start_plz, start_ort, ziel_plz, ziel_ort, abfahrt, ankunft, fahrzeug, gesamtvolumen, belegte_m3, freie_m3, max_umweg_km, preis_pro_m3, status</p>
        <textarea className="eb-input min-h-[100px] mono text-xs" placeholder="start_plz,start_ort,ziel_plz,ziel_ort,abfahrt,ankunft,fahrzeug,gesamtvolumen,belegte_m3,freie_m3,max_umweg_km,preis_pro_m3,status" value={csv} onChange={(e) => setCsv(e.target.value)} data-testid="route-csv-input" />
        <button onClick={doImport} className="eb-btn-outline mt-3" data-testid="route-import-btn"><Upload className="w-4 h-4" /> Importieren</button>
      </div>

      {loading ? <div className="text-slate-400">Lädt…</div> : (
        <div className="space-y-3">
          {own.map((t) => (
            <div key={t.id} className="eb-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`route-row-${t.id}`}>
              <div>
                <div className="flex items-center gap-2 font-head font-semibold flex-wrap">{t.start_ort} {(t.stops || []).map((s, i) => <span key={i} className="text-slate-400 text-sm">→ {s.ort}</span>)} → {t.ziel_ort} <span className={`eb-badge ${t.expired ? "bg-slate-700 text-slate-400" : t.status==="aktiv"?"bg-emerald-500/15 text-emerald-400":"bg-slate-700 text-slate-300"}`}>{t.expired ? "abgelaufen" : t.status}</span></div>
                <div className="text-sm text-slate-400 mt-1">{fmtDate(t.abfahrt)} · {t.vehicle_type} · <span className="mono text-emerald-400">{t.freie_m3} m³ frei</span> von {t.gesamt_volumen} · Umweg {t.max_umweg_km} km{t.preis_pro_m3?` · ${t.preis_pro_m3} €/m³`:""}</div>
              </div>
              <div className="flex gap-1.5 items-center">
                {t.pending_approval && <span className="eb-badge bg-amber-500/15 text-amber-400" data-testid={`route-pending-${t.id}`}>wartet auf Freigabe</span>}
                <ShareRoute trip={t} />
                <button onClick={() => { setEdit(t); setShow(false); }} className="eb-btn-ghost !px-2.5 !py-2" title="Bearbeiten" data-testid={`route-edit-${t.id}`}><FileText className="w-4 h-4" /></button>
                {t.status === "aktiv" ? (
                  <button onClick={() => toggle(t)} className="eb-btn-ghost !px-2.5 !py-2" title="Deaktivieren" data-testid={`route-toggle-${t.id}`}><Power className="w-4 h-4" /></button>
                ) : (
                  <button onClick={() => toggle(t)} className="eb-btn-primary !px-3 !py-2 text-xs" title="Route öffentlich sichtbar machen" data-testid={`route-golive-${t.id}`}><Power className="w-3.5 h-3.5" /> Live schalten</button>
                )}
                <button onClick={() => dup(t.id)} className="eb-btn-ghost !px-2.5 !py-2" title="Duplizieren" data-testid={`route-dup-${t.id}`}><Copy className="w-4 h-4" /></button>
                <button onClick={() => del(t.id)} className="eb-btn-ghost !px-2.5 !py-2 text-red-400" title="Löschen" data-testid={`route-del-${t.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Partners() {
  const { data, loading, reload } = useData("/admin/partners");
  const [busy, setBusy] = useState("");
  const decide = async (p, approve) => {
    let reason = "";
    if (!approve) { reason = window.prompt("Grund der Ablehnung (wird dem Partner mitgeteilt):", "Unterlagen unvollständig"); if (reason === null) return; }
    setBusy(p.id);
    try { await api.post(`/admin/partners/${p.id}/${approve ? "approve" : "reject"}`, approve ? {} : { reason }); toast.success(approve ? "Partner freigegeben" : "Partner abgelehnt"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy("");
  };
  if (loading) return <div className="text-slate-400">Lädt…</div>;
  const COLOR = { aktiv: "bg-emerald-500/15 text-emerald-400", pending: "bg-amber-500/15 text-amber-400", abgelehnt: "bg-red-500/15 text-red-400" };
  return (
    <div className="space-y-3" data-testid="admin-partners">
      {data.length === 0 ? <div className="eb-card p-8 text-center text-slate-500">Keine Partner</div> : data.map((p) => (
        <div key={p.id} className="eb-card p-5" data-testid={`admin-partner-${p.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2"><span className="font-head font-semibold">{p.company_name}</span><span className={`eb-badge ${COLOR[p.status] || "bg-slate-700 text-slate-300"}`} data-testid={`admin-partner-status-${p.id}`}>{p.status}</span></div>
              <div className="text-sm text-slate-400 mt-1">{p.contact_person} · {p.email} · {p.phone} · {p.trip_count ?? 0} Fahrten</div>
              <div className="grid sm:grid-cols-3 gap-x-6 gap-y-1 text-xs mt-3">
                {[["USt-ID", p.ust_id], ["Versicherung", p.insurance], ["Versicherung bis", p.insurance_valid_until ? `${p.insurance_valid_until.slice(0, 10)}${p.insurance_expired ? " (abgelaufen – Fahrten gesperrt)" : ""}` : null], ["Genehmigungen", p.permits], ["Fahrzeuge", p.vehicle_data], ["Adresse", p.address], ["Website", p.website]].map(([k, v]) => (
                  <div key={k}><span className="text-slate-500">{k}: </span><span className="text-slate-300">{v || "–"}</span></div>
                ))}
              </div>
              {p.documents?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">{p.documents.map((id, i) => <a key={id} href={`${API}/files/${id}`} target="_blank" rel="noreferrer" className="text-xs text-[#FF5A1F] underline flex items-center gap-1" data-testid={`admin-partner-doc-${id}`}><FileText className="w-3.5 h-3.5" /> Nachweis {i + 1}</a>)}</div>
              )}
              {p.reject_reason && p.status === "abgelehnt" && <div className="text-xs text-red-300 mt-2">Ablehnungsgrund: {p.reject_reason}</div>}
            </div>
            <div className="flex gap-2">
              {p.status !== "aktiv" && <button onClick={() => decide(p, true)} disabled={busy === p.id} className="eb-btn-primary !py-2 text-sm" data-testid={`admin-partner-approve-${p.id}`}><CheckCircle2 className="w-4 h-4" /> Freigeben</button>}
              {p.status !== "abgelehnt" && <button onClick={() => decide(p, false)} disabled={busy === p.id} className="eb-btn-outline !py-2 text-sm !border-red-500/40 !text-red-300" data-testid={`admin-partner-reject-${p.id}`}>Ablehnen</button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Payouts() {
  const { data, loading, reload } = useData("/admin/payouts");
  const [busy, setBusy] = useState("");
  const setStatus = async (id, status) => { await api.post(`/admin/payouts/${id}/status?status=${status}`); toast.success("Status aktualisiert"); reload(); };
  const execute = async (p) => {
    if (!window.confirm(`${p.auszahlungsbetrag} € als überwiesen markieren und Gutschrift an den Partner mailen?`)) return;
    setBusy(p.id);
    try { await api.post(`/admin/payouts/${p.id}/execute`); toast.success("Auszahlung ausgeführt – Gutschrift versendet"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy("");
  };
  const STATUSES = ["offen", "bezahlt", "transport_durchgefuehrt", "freigegeben", "ausgezahlt", "storniert"];
  if (loading) return <div className="text-slate-400">Lädt…</div>;
  const offen = data.filter((p) => !["ausgezahlt", "storniert"].includes(p.status)).reduce((s, p) => s + p.auszahlungsbetrag, 0);
  return (
    <div className="space-y-3" data-testid="admin-payouts">
      <div className="eb-card p-4 flex items-center justify-between"><span className="eb-label">Offene Auszahlungen</span><span className="font-head text-xl font-bold text-amber-400" data-testid="admin-payouts-open-sum">{Math.round(offen * 100) / 100} €</span></div>
      {data.length === 0 ? <div className="eb-card p-8 text-center text-slate-500">Keine Auszahlungen</div> : data.map((p) => (
        <div key={p.id} className="eb-card p-5 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            <div className="text-slate-400">Kunde <span className="mono text-slate-200">{p.kundenpreis} €</span> · Partner <span className="mono text-slate-200">{p.partnerpreis} €</span> · Provision <span className="mono text-emerald-400">{p.kommission} €</span></div>
            <div className="text-slate-500 mt-1">Auszahlungsbetrag: <span className="mono">{p.auszahlungsbetrag} €</span> (Gebühr {p.zahlungsgebuehr} €){p.credit_note_number && <> · Gutschrift <a href={`${API}/payouts/${p.id}/credit-note`} target="_blank" rel="noreferrer" className="text-[#FF5A1F] underline" data-testid={`payout-credit-note-${p.id}`}>{p.credit_note_number}</a></>}</div>
          </div>
          <div className="flex items-center gap-2">
            {!["ausgezahlt", "storniert"].includes(p.status) && <button onClick={() => execute(p)} disabled={busy === p.id} className="eb-btn-primary !py-2 text-sm" data-testid={`payout-execute-${p.id}`}><Wallet className="w-4 h-4" /> {busy === p.id ? "…" : "Als überwiesen markieren"}</button>}
            <select className="eb-input !w-auto" value={p.status} onChange={(e) => setStatus(p.id, e.target.value)} data-testid={`payout-status-${p.id}`}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      ))}
    </div>
  );
}

function Reviews() {
  const { data, loading, reload } = useData("/admin/reviews");
  const moderate = async (id, status) => { await api.post(`/admin/reviews/${id}/moderate?status=${status}`); toast.success("Bewertung " + status); reload(); };
  if (loading) return <div className="text-slate-400">Lädt…</div>;
  return (
    <div className="space-y-3" data-testid="admin-reviews">
      {data.length === 0 ? <div className="eb-card p-8 text-center text-slate-500">Keine Bewertungen</div> : data.map((r) => (
        <div key={r.id} className="eb-card p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">{[1,2,3,4,5].map(s=><Star key={s} className={`w-4 h-4 ${s<=r.rating?"text-amber-400 fill-amber-400":"text-slate-600"}`} />)}</div>
            <span className={`eb-badge ${r.status==="approved"?"bg-emerald-500/15 text-emerald-400":r.status==="rejected"?"bg-red-500/15 text-red-400":"bg-amber-500/15 text-amber-400"}`}>{r.status}</span>
          </div>
          <p className="text-sm text-slate-300 mt-2">{r.text || "(kein Text)"}</p>
          <div className="text-xs text-slate-500 mt-1">{r.customer_name} → {r.partner_name}</div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => moderate(r.id, "approved")} className="eb-btn-outline !py-1.5 text-xs" data-testid={`review-approve-${r.id}`}>Freigeben</button>
            <button onClick={() => moderate(r.id, "rejected")} className="eb-btn-ghost !py-1.5 text-xs text-red-400" data-testid={`review-reject-${r.id}`}>Ablehnen</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Cms() {
  const { data, loading, reload } = useData("/admin/seo");
  const [f, setF] = useState({ slug: "", from_city: "", to_city: "", h1: "", description: "", distance_km: "", typical_time: "", published: true });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.slug || !f.h1) return toast.error("Slug und H1 erforderlich");
    await api.post("/admin/seo", { ...f, distance_km: f.distance_km ? parseFloat(f.distance_km) : null, faq: [] });
    toast.success("SEO-Seite gespeichert"); setF({ slug: "", from_city: "", to_city: "", h1: "", description: "", distance_km: "", typical_time: "", published: true }); reload();
  };
  const del = async (slug) => { await api.delete(`/admin/seo/${slug}`); reload(); };
  return (
    <div data-testid="admin-cms">
      <div className="eb-card p-6 mb-6">
        <h3 className="font-head font-semibold mb-4">SEO-Landingpage anlegen / aktualisieren</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="eb-label">Slug (z.B. beiladung-koeln-muenchen)</label><input className="eb-input mt-1.5" value={f.slug} onChange={set("slug")} data-testid="cms-slug" /></div>
          <div><label className="eb-label">H1</label><input className="eb-input mt-1.5" value={f.h1} onChange={set("h1")} data-testid="cms-h1" /></div>
          <div><label className="eb-label">Von Stadt</label><input className="eb-input mt-1.5" value={f.from_city} onChange={set("from_city")} data-testid="cms-from" /></div>
          <div><label className="eb-label">Nach Stadt</label><input className="eb-input mt-1.5" value={f.to_city} onChange={set("to_city")} data-testid="cms-to" /></div>
          <div><label className="eb-label">Entfernung km</label><input className="eb-input mt-1.5" value={f.distance_km} onChange={set("distance_km")} data-testid="cms-dist" /></div>
          <div><label className="eb-label">Typische Fahrzeit</label><input className="eb-input mt-1.5" value={f.typical_time} onChange={set("typical_time")} data-testid="cms-time" /></div>
          <div className="sm:col-span-2"><label className="eb-label">Beschreibung</label><textarea className="eb-input mt-1.5 min-h-[80px]" value={f.description} onChange={set("description")} data-testid="cms-desc" /></div>
        </div>
        <button onClick={save} className="eb-btn-primary mt-4" data-testid="cms-save"><Plus className="w-4 h-4" /> Speichern</button>
      </div>
      {loading ? <div className="text-slate-400">Lädt…</div> : (
        <div className="space-y-2">
          {data.map((p) => (
            <div key={p.slug} className="eb-card p-4 flex items-center justify-between">
              <div><div className="font-semibold text-slate-100">/{p.slug}</div><div className="text-sm text-slate-400">{p.h1}</div></div>
              <div className="flex items-center gap-2">
                <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="eb-btn-ghost !py-1.5 text-xs">Ansehen</a>
                <button onClick={() => del(p.slug)} className="eb-btn-ghost !py-1.5 text-red-400" data-testid={`cms-del-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <div className="text-slate-400">Lädt…</div>;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const save = async () => {
    await api.put("/admin/settings", {
      commission_percent: parseFloat(s.commission_percent), deposit_percent: parseFloat(s.deposit_percent),
      base_price_per_m3: parseFloat(s.base_price_per_m3), payment_gateways: s.payment_gateways || ["stripe", "paypal"],
      reply_templates: (s.reply_templates || []).filter((t) => t.label.trim() && t.text.trim()),
    });
    toast.success("Einstellungen gespeichert");
  };
  const tpls = s.reply_templates || [];
  const setTpl = (i, k, v) => setS({ ...s, reply_templates: tpls.map((t, j) => (j === i ? { ...t, [k]: v } : t)) });
  return (
    <div className="space-y-6">
    <div className="eb-card p-6 max-w-xl" data-testid="admin-settings">
      <h3 className="font-head font-semibold mb-5">Preis- & Zahlungslogik</h3>
      <div className="space-y-4">
        <div><label className="eb-label">Easy-Beiladung-Provision (%)</label><input type="number" className="eb-input mt-1.5" value={s.commission_percent} onChange={set("commission_percent")} data-testid="settings-commission" /></div>
        <div><label className="eb-label">Anzahlung (%)</label><input type="number" className="eb-input mt-1.5" value={s.deposit_percent} onChange={set("deposit_percent")} data-testid="settings-deposit" /></div>
        <div><label className="eb-label">Basispreis pro m³ (€, wenn Partner keinen Preis hinterlegt)</label><input type="number" className="eb-input mt-1.5" value={s.base_price_per_m3} onChange={set("base_price_per_m3")} data-testid="settings-baseprice" /></div>
      </div>
      <button onClick={save} className="eb-btn-primary mt-6" data-testid="settings-save">Speichern</button>
    </div>
    <div className="eb-card p-6 max-w-3xl" data-testid="admin-templates">
      <h3 className="font-head font-semibold mb-1">Schnellantworten (Anfrage-Chat)</h3>
      <p className="text-xs text-slate-500 mb-5">Platzhalter: <code className="mono">{"{vorname}"}</code>, <code className="mono">{"{name}"}</code>, <code className="mono">{"{route}"}</code>, <code className="mono">{"{volumen}"}</code></p>
      <div className="space-y-4">
        {tpls.map((t, i) => (
          <div key={i} className="rounded-lg border border-slate-800 p-3 space-y-2" data-testid={`template-row-${i}`}>
            <div className="flex gap-2">
              <input className="eb-input flex-1" placeholder="Bezeichnung" value={t.label} onChange={(e) => setTpl(i, "label", e.target.value)} data-testid={`template-label-${i}`} />
              <button onClick={() => setS({ ...s, reply_templates: tpls.filter((_, j) => j !== i) })} className="eb-btn-ghost text-red-400 !px-2.5" title="Vorlage entfernen" data-testid={`template-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <textarea className="eb-input min-h-[88px] text-sm" value={t.text} onChange={(e) => setTpl(i, "text", e.target.value)} data-testid={`template-text-${i}`} />
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={() => setS({ ...s, reply_templates: [...tpls, { label: "", text: "" }] })} className="eb-btn-outline" data-testid="template-add"><Plus className="w-4 h-4" /> Vorlage hinzufügen</button>
        <button onClick={save} className="eb-btn-primary" data-testid="templates-save">Speichern</button>
      </div>
    </div>
    </div>
  );
}
